import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Ban, Loader2 } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { PageHeader } from "../../components/dash/ui";
import { api, formatApiError } from "../../lib/api";
import { formatDate } from "../../lib/labels";
import { parseCoords } from "../../components/map/LandMap";

const TYPES = { PHONE: "Nomor HP / WhatsApp", NIK: "NIK (16 angka)", LOCATION: "Lokasi (titik + radius)" };
const F0 = { type: "PHONE", value: "", coords: "", radiusM: 150, label: "", reason: "" };

const Existing = ({ list, testid }) => (list?.length ? (
  <ul className="mt-2 text-xs text-red-800 space-y-0.5" data-testid={testid}>
    {list.map((e) => <li key={e.id}>Data yang masih berjalan: <Link to={e.link} className="underline font-semibold">{e.label} {e.number} · {e.name}</Link> ({e.status})</li>)}
  </ul>) : null);

function AddForm({ onDone }) {
  const [f, setF] = useState(F0);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const save = async () => {
    const body = { type: f.type, value: f.value, label: f.label, reason: f.reason, radiusM: Number(f.radiusM) || 150 };
    if (f.type === "LOCATION") {
      const p = parseCoords(f.coords);
      if (p.error) { toast.error(p.error); return; }
      [body.latitude, body.longitude] = p.ll;
    }
    setBusy(true);
    try {
      const r = await api.post("/admin/watchlist", body);
      toast.success(r.data.existing.length ? `Ditambahkan · ${r.data.existing.length} data yang sedang berjalan cocok` : "Ditambahkan ke Daftar Hitam");
      setF(F0); onDone();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="bg-white border-2 border-red-200 rounded-xl p-4 space-y-3" data-testid="watch-form">
      <h2 className="font-bold text-slate-900">Tambah ke Daftar Hitam</h2>
      <div className="grid sm:grid-cols-2 gap-3">
        <label className="text-sm"><span className="font-semibold text-slate-700">Jenis</span>
          <select value={f.type} onChange={set("type")} className="w-full h-9 rounded-md border border-slate-200 px-2 text-sm" data-testid="watch-type">
            {Object.entries(TYPES).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></label>
        {f.type === "LOCATION" ? (
          <div className="grid grid-cols-3 gap-2">
            <label className="text-sm col-span-2"><span className="font-semibold text-slate-700">Titik (lat, lng)</span><Input value={f.coords} onChange={set("coords")} placeholder="-0.061575, 100.486738" data-testid="watch-coords" /></label>
            <label className="text-sm"><span className="font-semibold text-slate-700">Radius (m)</span><Input type="number" value={f.radiusM} onChange={set("radiusM")} data-testid="watch-radius" /></label>
          </div>
        ) : <label className="text-sm"><span className="font-semibold text-slate-700">{TYPES[f.type]}</span><Input value={f.value} onChange={set("value")} placeholder={f.type === "NIK" ? "16 angka" : "0812xxxx atau +62812xxxx"} data-testid="watch-value" /></label>}
        <label className="text-sm"><span className="font-semibold text-slate-700">Keterangan singkat (opsional)</span><Input value={f.label} onChange={set("label")} placeholder="mis. lahan sengketa Nagari X" data-testid="watch-label" /></label>
      </div>
      <label className="text-sm block"><span className="font-semibold text-slate-700">Alasan (wajib, min. 5 karakter)</span><Textarea rows={2} value={f.reason} onChange={set("reason")} data-testid="watch-reason" /></label>
      <Button type="button" disabled={busy || f.reason.trim().length < 5} className="bg-red-700 hover:bg-red-800" onClick={save} data-testid="watch-save">{busy && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}<Ban className="h-4 w-4 mr-1" />Tandai "Jangan Diproses"</Button>
    </div>
  );
}

function Entry({ w, reload }) {
  const [note, setNote] = useState("");
  const [open, setOpen] = useState(false);
  const off = async () => {
    try { await api.post(`/admin/watchlist/${w.id}/deactivate`, { note }); toast.success("Entri dinonaktifkan"); reload(); } catch (e) { toast.error(formatApiError(e)); }
  };
  const what = w.type === "LOCATION" ? `${w.latitude}, ${w.longitude} · radius ${w.radiusM} m` : w.value;
  return (
    <li className={`rounded-xl border p-4 text-sm ${w.active ? "bg-red-50 border-red-300" : "bg-slate-50 border-slate-200 text-slate-500"}`} data-testid={`watch-${w.id}`}>
      <div className="flex flex-wrap justify-between gap-2">
        <p className="font-semibold break-words">{TYPES[w.type]}: <span className="font-mono">{what}</span>{w.label ? ` · ${w.label}` : ""}</p>
        <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${w.active ? "bg-red-600 text-white" : "bg-slate-300"}`}>{w.active ? "AKTIF" : "NONAKTIF"}</span>
      </div>
      <p className="text-xs mt-1 break-words">Alasan: {w.reason} · {w.createdByName} · {formatDate(w.createdAt)}</p>
      {!w.active && <p className="text-xs break-words">Dinonaktifkan {w.deactivatedBy} {formatDate(w.deactivatedAt)}: {w.deactivationNote}</p>}
      <Existing list={w.existing} testid={`watch-existing-${w.id}`} />
      {w.active && (open ? (
        <div className="mt-2 flex flex-wrap gap-2"><Input className="max-w-sm" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Alasan menonaktifkan" data-testid={`watch-off-note-${w.id}`} />
          <Button size="sm" variant="outline" disabled={note.trim().length < 5} onClick={off} data-testid={`watch-off-save-${w.id}`}>Nonaktifkan</Button>
          <Button size="sm" variant="ghost" onClick={() => { setOpen(false); setNote(""); }} data-testid={`watch-off-cancel-${w.id}`}>Batal</Button></div>
      ) : <Button size="sm" variant="outline" className="mt-2" onClick={() => setOpen(true)} data-testid={`watch-off-${w.id}`}>Nonaktifkan entri</Button>)}
    </li>
  );
}

export default function AdminWatchlist() {
  const [data, setData] = useState(null);
  const load = useCallback(() => { api.get("/admin/watchlist").then((r) => setData(r.data)).catch((e) => toast.error(formatApiError(e))); }, []);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="space-y-5" data-testid="watchlist-page">
      <PageHeader title="Daftar Hitam" subtitle="Nomor HP, NIK, atau lokasi yang tidak boleh diproses walau belum pernah ditolak. Pengajuan/pendaftaran yang cocok mendapat peringatan merah, notifikasi dan email ke Owner, dan tidak bisa ditindaklanjuti, disetujui, dijadikan lahan, atau diverifikasi selama entri aktif. Hanya terlihat oleh PT SJI." />
      <AddForm onDone={load} />
      {!data ? <Loader2 className="h-5 w-5 animate-spin" /> : !data.items.length ? <p className="text-sm text-slate-500" data-testid="watch-empty">Daftar Hitam masih kosong.</p>
        : <ul className="space-y-3" data-testid="watch-list">{data.items.map((w) => <Entry key={w.id} w={w} reload={load} />)}</ul>}
    </div>
  );
}
